import React from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ExternalLink } from "lucide-react";

// Curated summary of the most-referenced FDA / ISO regulations for medical devices.
// Not exhaustive; unknown citations fall back to the generic "FDA regulation" label.
export const CFR_DICT = {
  "21 CFR 807": { title: "Establishment Registration and Device Listing / 510(k)", body: "Sets out requirements for device establishment registration and premarket notification (510(k))." },
  "21 CFR 807.87": { title: "Content of 510(k) submission", body: "Specifies the required contents of a 510(k), including SE comparison to a predicate." },
  "21 CFR 807.92": { title: "510(k) Summary content", body: "Defines what must appear in the 510(k) Summary posted publicly by FDA." },
  "21 CFR 812": { title: "Investigational Device Exemptions (IDE)", body: "Governs clinical investigations of medical devices; premarket data used to support 510(k)/PMA." },
  "21 CFR 814": { title: "Premarket Approval (PMA)", body: "Application, review, and approval process for Class III devices requiring FDA approval." },
  "21 CFR 820": { title: "Quality System Regulation (QSR)", body: "The umbrella regulation for medical device quality systems, including design controls, CAPA, records." },
  "21 CFR 820.20": { title: "Management responsibility", body: "Requires management to establish, review, and maintain the quality system." },
  "21 CFR 820.30": { title: "Design controls", body: "Requires design & development planning, inputs, outputs, review, verification, validation, transfer, changes, and DHF." },
  "21 CFR 820.30(j)": { title: "Design History File (DHF)", body: "Requires each manufacturer to establish and maintain a DHF for each type of device." },
  "21 CFR 820.100": { title: "Corrective and Preventive Action (CAPA)", body: "Requires procedures for identifying and correcting nonconformities and preventing recurrence." },
  "21 CFR 820.198": { title: "Complaint files", body: "Requires procedures for receiving, reviewing, and evaluating complaints by a formally designated unit." },
  "21 CFR 801": { title: "Labeling", body: "General labeling requirements for medical devices, including adequate directions for use." },
  "21 CFR 803": { title: "Medical Device Reporting (MDR)", body: "Adverse event reporting: manufacturers/importers/user facilities must report deaths, serious injuries, malfunctions." },
  "21 CFR 806": { title: "Corrections and Removals", body: "Requires reporting of corrections and removals initiated to reduce risk to health." },
  "21 CFR 860.200": { title: "De Novo Classification Requests", body: "Pathway for novel low-to-moderate-risk devices with no legally marketed predicate." },
  "21 CFR 830": { title: "Unique Device Identification (UDI)", body: "Requirements for the Unique Device Identifier and Global UDI Database (GUDID)." },
  "ISO 14971": { title: "Risk management for medical devices", body: "International standard for applying risk management throughout the device lifecycle." },
  "ISO 13485": { title: "QMS for medical devices", body: "International QMS standard commonly recognized by FDA for design and manufacturing." },
  "IEC 62304": { title: "Medical device software lifecycle", body: "Life cycle requirements for medical device software development and maintenance." },
  "IEC 62366": { title: "Usability engineering for medical devices", body: "Process for design and evaluation of usability as related to safety." },
  "ISO 10993": { title: "Biological evaluation of medical devices", body: "Biocompatibility assessment framework for materials in contact with the body." },
  "ISO 15223-1": { title: "Symbols for medical device labels", body: "Standardized symbols used on medical device labels and IFUs." },
};

function _lookup(ref) {
  // Try progressively broader keys: "21 CFR 820.30(j)" → "21 CFR 820.30" → "21 CFR 820"
  const candidates = [ref];
  const cfrMatch = ref.match(/^(21 CFR \d+)(\.\d+)?/);
  if (cfrMatch) {
    if (cfrMatch[2]) candidates.push(cfrMatch[1] + cfrMatch[2]);
    candidates.push(cfrMatch[1]);
  }
  const isoMatch = ref.match(/^(ISO|IEC) \d+/);
  if (isoMatch) candidates.push(isoMatch[0]);
  for (const c of candidates) if (CFR_DICT[c]) return { ...CFR_DICT[c], match: c };
  return null;
}

function _ecfrUrl(ref) {
  const m = ref.match(/^21 CFR (\d+)(?:\.(\d+))?/);
  if (m) {
    const part = m[1];
    const section = m[2];
    if (section) return `https://www.ecfr.gov/current/title-21/chapter-I/subchapter-H/part-${part}/section-${part}.${section}`;
    return `https://www.ecfr.gov/current/title-21/chapter-I/subchapter-H/part-${part}`;
  }
  return `https://www.google.com/search?q=${encodeURIComponent(ref + " medical device standard")}`;
}

export default function CfrPopover({ reg }) {
  const info = _lookup(reg);
  const url = _ecfrUrl(reg);
  return (
    <HoverCard openDelay={80} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          data-testid="cfr-tag"
          className="cursor-help font-mono text-[0.85em] text-[#93C5FD] underline decoration-[#2563EB]/50 decoration-dotted underline-offset-2 hover:decoration-solid hover:text-[#DBEAFE]"
        >
          {reg}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        className="w-80 border border-slate-200 bg-slate-50 p-4 text-slate-900 shadow-xl"
      >
        <div className="overline mb-1 text-[#2563EB]">{info?.match || "REFERENCE"}</div>
        <div className="font-serif text-base font-semibold leading-tight">
          {info?.title || "FDA / ISO regulation"}
        </div>
        {info?.body && (
          <p className="mt-2 text-xs leading-relaxed text-slate-600">{info.body}</p>
        )}
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#93C5FD] hover:text-slate-900"
        >
          Open on eCFR <ExternalLink className="h-3 w-3" />
        </a>
      </HoverCardContent>
    </HoverCard>
  );
}
